import { Box, Stack, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import { Paths } from "src/constants/paths";
import { useIsMobile } from "src/hooks/useIsMobile";
import MobilePatientAccountNav from "src/pages/PatientDashboard/PatientAccount/MobilePatientAccountNav";
import ListIcon from "@mui/icons-material/List";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { CreditCardRounded } from "@mui/icons-material";
import MedicationIcon from "@mui/icons-material/Medication";
import MedicationOutlinedIcon from "@mui/icons-material/MedicationOutlined";
import { useTranslation } from "react-i18next";
import Resources from "src/@types/resources";
import { usePatientSubscriptions } from "src/context/PatientSubscriptionsProvider";
import { useGetChargesQuery } from "src/api/services/chargesApi";
import MessageIcon from "src/assets/icons/MessageIcon";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import LocalPharmacyOutlinedIcon from "@mui/icons-material/LocalPharmacyOutlined";
import { useMessageThreads } from "src/context/MessageThreadsProvider";

interface NavLinkItem {
  name: keyof Resources["translation"]["account"]["navBar"];
  icon: React.ElementType;
  activeIcon?: React.ElementType;
  link?: string;
  category?: string;
}

const pathsArray: NavLinkItem[] = [
  {
    name: `account`,
    icon: PersonOutlineOutlinedIcon,
    activeIcon: PersonIcon,
    link: Paths.MY_ACCOUNT_OVERVIEW,
  },
  {
    name: `subscriptions`,
    icon: MedicationOutlinedIcon,
    activeIcon: MedicationIcon,
    link: Paths.MY_SUBSCRIPTIONS,
  },
  {
    name: `preferred-pharmacy`,
    icon: LocalPharmacyOutlinedIcon,
    link: Paths.MY_ACCOUNT_PREFERRED_PHARMACY,
  },
  {
    name: `transaction-history`,
    icon: ListIcon,
    link: Paths.MY_ACCOUNT_TRANSACTION_HISTORY,
  },
  {
    name: `insurance-details`,
    icon: BadgeOutlinedIcon,
    link: Paths.MY_ACCOUNT_INSURANCE_DETAILS,
  },
  {
    name: `payment-methods`,
    icon: CreditCardRounded,
    link: Paths.MY_PAYMENT_METHODS,
  },

  {
    name: `help-center`,
    icon: InfoOutlinedIcon,
    link: `${Paths.MY_ACCOUNT_HELP_CENTER}?section=Medical+Care`,
    category: "Weight Loss",
  },
  {
    name: `messages`,
    icon: MessageIcon,
    link: "messages",
  },
  {
    name: `logout`,
    icon: LogoutIcon,
  },
];

interface Props1 {
  path: NavLinkItem;
}

const NavBarLink = ({ path }: Props1) => {
  const page = "account";
  const component = "navBar";
  const { t } = useTranslation();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const { name, icon, link, activeIcon } = path;

  const handleLogout = useCallback(() => navigate("/logout"), [navigate]);

  const active = useMemo(() => {
    const fullPath = pathname + search;

    return link && path && fullPath.includes(link?.split("?")[0] ?? "");
  }, [link, path, pathname, search]);

  const IconComponent = active && activeIcon ? activeIcon : icon;

  return (
    <Box
      key={name}
      sx={{
        borderLeft: active
          ? "3px solid var(--Primary-Primary-500, #00B6B9)"
          : "",
        width: "270px",
        padding: "16px",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={2}
        ml={1}
        sx={{
          backdropFilter: "blur(12px)",
          alignSelf: "stretch",
        }}
      >
        <IconComponent
          fontSize="medium"
          sx={{
            color: active ? "#00B6B9" : "#6C7278",
          }}
        />
        {name !== `logout` ? (
          <Link
            to={path.link || ""}
            style={{
              fontSize: "1.1rem",
              fontWeight: active ? 700 : 500,
              color: active ? "#00B6B9" : "#6C7278",
            }}
          >
            {t(`${page}.${component}.${name}`)}
          </Link>
        ) : (
          <Typography
            fontSize="1.1rem"
            fontWeight={600}
            color="#6C7278"
            onClick={handleLogout}
            sx={{ cursor: "pointer" }}
          >
            {t(`${page}.${component}.${name}`)}
          </Typography>
        )}
      </Stack>
    </Box>
  );
};

const PatientAccountNavBar = () => {
  const isMobile = useIsMobile();
  const { pathname } = useLocation();
  const { threads } = useMessageThreads(false);
  const categories = usePatientSubscriptions((sub) =>
    sub.map((s) => s.product_category.name),
  );
  const { data: charges = [] } = useGetChargesQuery();

  const chargesProductCategories = useMemo(
    () => charges.map((c) => c.product_category.name),
    [charges],
  );

  const chargeProductInsurance = useMemo(
    () => charges.find((c) => c.product.insurance_based),
    [charges],
  );

  const chargeProductEPrescribe = useMemo(
    () => charges.find((c) => c.product.is_e_prescribe),
    [charges],
  );

  const hideMobileAccountNav = [
    Paths.MY_ACCOUNT_OVERVIEW,
    Paths.MY_ACCOUNT_TRANSACTION_HISTORY,
    Paths.MY_ACCOUNT_HELP_CENTER,
    Paths.MY_PAYMENT_METHODS,
    Paths.MY_SUBSCRIPTIONS,
    "messages",
    Paths.MY_ACCOUNT_INSURANCE_DETAILS,
    Paths.MY_ACCOUNT_PREFERRED_PHARMACY,
  ].some((p) => pathname.includes(p));

  const filteredPaths = useMemo(() => {
    return pathsArray.filter((p) => {
      if (p.category === "Weight Loss") {
        return (
          categories.includes("Weight Loss") ||
          chargesProductCategories.includes("Weight Loss")
        );
      }

      if (p.name === "preferred-pharmacy") {
        return chargeProductEPrescribe != null;
      }

      if (p.name === "insurance-details") {
        return chargeProductInsurance != null;
      }

      if (p.name === "messages") {
        return threads.length > 0;
      }

      return true;
    });
  }, [
    categories,
    chargeProductEPrescribe,
    chargeProductInsurance,
    chargesProductCategories,
    threads.length,
  ]);

  return isMobile ? (
    <>
      {!hideMobileAccountNav ? <MobilePatientAccountNav /> : null}
      <Outlet />
    </>
  ) : (
    <Stack direction="row" gap={4}>
      <Stack
        position="sticky"
        top={95}
        sx={{
          boxShadow: "0px 3px 17px 0px rgba(0, 0, 0, 0.10)",
          borderRadius: "10px",
          padding: "16px 0px",
          maxWidth: "380px",
          height: "fit-content",
        }}
      >
        {filteredPaths.map((path) => (
          <NavBarLink key={path.name} path={path} />
        ))}
      </Stack>

      <Outlet />
    </Stack>
  );
};

export default PatientAccountNavBar;
